// src/context/AppProvider.tsx - ENHANCED: Billing integration with insufficient balance modal
import { useReducer, useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AppContext, appReducer, initialState } from './AppContext';
import { useJobPolling } from '../hooks/useJobPolling';
import type {
  AppContextType,
  ToastMessage,
  ConfirmationDialog,
  SortingState,
  FilterValue,
  DataSourceType,
  SearchResponse,
  CreateJobResponse,
  NavigationState,
  Job
} from '../types';
import { generateId } from '../utils';
import { API_BASE_URL } from '../constants';
import { useAuth } from '../hooks/useAuth';
import InsufficientBalanceModal from '../components/InsufficientBalanceModal';

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { apiKey } = useAuth();
  const stateRef = useRef(state);
  stateRef.current = state;

  // State for insufficient balance modal
  const [insufficientBalanceModal, setInsufficientBalanceModal] = useState({
    isOpen: false,
    currentBalance: 0,
    requiredAmount: 0
  });

  // Track recent toasts to prevent duplicates
  const recentToastsRef = useRef<Map<string, number>>(new Map());

  const showToast = useCallback((toast: Omit<ToastMessage, 'id' | 'timestamp'>) => {
    // Create a key based on toast content to detect duplicates
    const toastKey = `${toast.type}-${toast.title}-${toast.message}`;
    const now = Date.now();
    const recentToasts = recentToastsRef.current;
    
    // Check if the same toast was shown recently (within 2 seconds)
    if (recentToasts.has(toastKey)) {
      const lastShown = recentToasts.get(toastKey)!;
      if (now - lastShown < 2000) {
        console.log(`🚫 Duplicate toast prevented: ${toast.title}`);
        return; // Don't show duplicate toast
      }
    }
    
    // Record this toast and clean up old entries
    recentToasts.set(toastKey, now);
    
    // Clean up entries older than 5 seconds
    for (const [key, timestamp] of recentToasts.entries()) {
      if (now - timestamp > 5000) {
        recentToasts.delete(key);
      }
    }
    
    const toastMessage: ToastMessage = { ...toast, id: generateId(), timestamp: Date.now() };
    dispatch({ type: 'ADD_TOAST', payload: toastMessage });
  }, []);

  const showConfirmationDialog = useCallback((dialog: Omit<ConfirmationDialog, 'isOpen'>) => {
    dispatch({ type: 'SHOW_CONFIRMATION_DIALOG', payload: { ...dialog, isOpen: true } });
  }, []);

  const hideConfirmationDialog = useCallback(() => {
    dispatch({ type: 'HIDE_CONFIRMATION_DIALOG' });
  }, []);

  const getCompleteFilters = useCallback((): Record<string, unknown> => {
    const currentState = stateRef.current;
    let filters = { ...currentState.searchState.filters };

    if (currentState.navigationState.view === 'list' && currentState.navigationState.listId) {
      filters = {
        ...filters,
        list_ids: { type: 'picklist', values: [currentState.navigationState.listId], operator: 'OR' }
      };
    }

    return filters;
  }, []);

  const getVisibleUUIDs = useCallback((): string[] => {
    const currentState = stateRef.current;
    return currentState.contacts
      .map(contact => contact.UUID)
      .filter((uuid): uuid is string => Boolean(uuid));
  }, []);

  const refreshCurrentView = useCallback(async () => {
    try {
      console.log('🔄 Refreshing current view while preserving state...');

      const currentState = stateRef.current;
      const { searchState, pageSize, schema } = currentState;

      const completeFilters = getCompleteFilters();

      const apiSearchState: Record<string, unknown> = {
        query: "",
        page: searchState.page,
        size: pageSize,
        filters: Object.keys(completeFilters).length > 0 ? completeFilters : undefined,
        data_source: searchState.dataSource,
      };

      if (searchState.sorting && searchState.sorting.length > 0 && schema) {
        const columnId = searchState.sorting[0].id;
        const columnConfig = schema.column_configs?.find(col => col.id === columnId);
        if (columnConfig) {
          apiSearchState.sort_field = columnConfig.sort_field;
          apiSearchState.sort_order = searchState.sorting[0].desc ? 'desc' : 'asc';
        }
      }

      console.log('🔍 Refreshing with preserved state:', apiSearchState);

      const response = await fetch(`${API_BASE_URL}/api/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
        body: JSON.stringify(apiSearchState)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `API request failed: ${response.status} ${response.statusText}`);
      }

      const result: SearchResponse = await response.json();

      if (result) {
        dispatch({
          type: 'SET_SEARCH_RESULTS',
          payload: {
            contacts: result.hits,
            total: result.total,
            totalPages: result.total_pages,
            page: result.page,
            searchTime: result.query_time_ms,
            dataSource: searchState.dataSource
          }
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Could not refresh the view.';
      console.error('❌ Failed to refresh current view:', error);
      showToast({
        type: 'error',
        title: 'Refresh Failed',
        message: errorMessage
      });
    }
  }, [apiKey, showToast, getCompleteFilters]);

  const onJobsUpdate = useCallback((updatedJobs: Job[]) => {
    console.log('🔄 Updating active jobs:', updatedJobs.map(j => ({ id: j.job_id, status: j.status, type: j.job_type })));
    dispatch({ type: 'SET_ACTIVE_JOBS', payload: updatedJobs });
  }, []);

  // ✅ FIXED: Bulletproof job completion handler with better logging
  const onJobComplete = useCallback((completedJob: Job, dismiss: () => void) => {
    console.log(`🏁 Job completed: ${completedJob.job_id} (${completedJob.status})`);

    const currentState = stateRef.current;
    const isContactsOperation = ['add_to_list', 'delete_contacts', 'remove_from_list'].includes(completedJob.job_type);
    const isPeopleOperation = completedJob.job_type === 'contacts_unlock';
    const currentDataSource = currentState.searchState.dataSource;

    // ✅ CRITICAL FIX: Clear processing state for ANY completed job that might have processing UUIDs
    const isFinalStatus = ['completed', 'failed', 'canceled'].includes(completedJob.status);
    if (isFinalStatus && currentState.processing.uuids.size > 0) {
      console.log(`🧹 Current processing state:`, {
        uuids: currentState.processing.uuids.size,
        operation: currentState.processing.operation,
        jobId: currentState.processing.jobId
      });

      // ✅ ENHANCED: More forgiving matching logic
      const shouldClearProcessing = (
        // Direct job ID match
        currentState.processing.jobId === completedJob.job_id ||
        // Pending job ID (for quick completions)
        currentState.processing.jobId === 'pending' ||
        // Operation type match
        (currentState.processing.operation === 'delete' && ['delete_contacts', 'remove_from_list'].includes(completedJob.job_type))
      );

      if (shouldClearProcessing) {
        console.log(`✅ Clearing processing state for ${completedJob.status} job: ${completedJob.job_id}`);
        dispatch({ type: 'CLEAR_PROCESSING' });
      } else {
        console.warn(`⚠️ Processing state not cleared - no match for job ${completedJob.job_id}`);
      }
    }

    // Refresh view for contacts operations
    if (isContactsOperation && currentDataSource === 'contacts') {
      console.log(`🔄 Refreshing contacts view after ${completedJob.job_type} job ${completedJob.status}`);
      setTimeout(() => refreshCurrentView(), 500);
    } else if (isPeopleOperation) {
      console.log(`🔓 People operation ${completedJob.status}, refreshing lists`);
      setTimeout(() => {
        const event = new Event('lists-refresh');
        window.dispatchEvent(event);
      }, 500);
    }

    // Better status-specific messaging
    if (completedJob.status === 'failed') {
      showToast({
        type: 'error',
        title: 'Operation Failed',
        message: completedJob.error || 'The operation failed to complete',
        duration: 7000
      });
    } else if (completedJob.status === 'completed') {
      // Show success toast with job-specific cost (not including debt rollover)
      let message = 'Operation completed successfully';
      let cost = null;
      
      if (completedJob.result?.billing?.job_cost) {
        cost = parseFloat(completedJob.result.billing.job_cost);
      } else if (completedJob.result?.billing?.amount_spent) {
        cost = parseFloat(completedJob.result.billing.amount_spent);
      }
      
      if (cost !== null) {
        message += ` • Cost: $${cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      
      showToast({
        type: 'success',
        title: 'Operation Completed',
        message: message,
        duration: 5000
      });
    }

    // Faster dismiss for cancelled jobs
    const dismissDelay = completedJob.status === 'canceled' ? 1500 : 4500;
    setTimeout(() => dismiss(), dismissDelay);
  }, [refreshCurrentView, showToast]);

  const { addJobId, cancelJob, dismissJob } = useJobPolling({
    apiKey: apiKey || '',
    onJobsUpdate,
    onJobComplete,
  });

  const removeJob = useCallback((jobId: string) => {
    dismissJob(jobId);
  }, [dismissJob]);

  // ✅ ENHANCED: Better job request with immediate state tracking
  const makeJobRequest = useCallback(async (
    endpoint: string,
    requestBody: Record<string, unknown>,
    jobType: Job['job_type']
  ): Promise<string | null> => {
    const currentState = stateRef.current;
    let processingUuids: string[] = [];

    // Determine which UUIDs to track
    if (['delete_contacts', 'remove_from_list', 'contacts_unlock'].includes(jobType)) {
      if (currentState.selection.selectAll) {
        processingUuids = getVisibleUUIDs();
        console.log(`🔄 Using visible UUIDs for "Select All" processing: ${processingUuids.length} items`);
      } else {
        processingUuids = Array.from(currentState.selection.selectedIds);
        console.log(`🔄 Using selected UUIDs for processing: ${processingUuids.length} items`);
      }
    }

    // ✅ FIXED: Clear ANY existing processing state first
    if (currentState.processing.uuids.size > 0) {
      console.log(`🧹 Clearing existing processing state (${currentState.processing.uuids.size} UUIDs)`);
      dispatch({ type: 'CLEAR_PROCESSING' });
    }

    // Set temporary processing state with a placeholder job ID
    if (processingUuids.length > 0) {
      let operation: 'delete' | null = null;

      if (['delete_contacts', 'remove_from_list'].includes(jobType)) {
        operation = 'delete';
      }

      if (operation) {
        const tempJobId = `pending-${Date.now()}`;
        console.log(`🔄 Setting processing state: ${processingUuids.length} UUIDs, operation: ${operation}, tempJobId: ${tempJobId}`);
        dispatch({
          type: 'SET_PROCESSING',
          payload: {
            uuids: processingUuids,
            operation,
            jobId: tempJobId
          }
        });
      }
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Clear processing state on error
        console.log(`❌ Job creation failed, clearing processing state`);
        dispatch({ type: 'CLEAR_PROCESSING' });
        throw new Error(errorData.detail || `Job creation failed: ${response.status}`);
      }

      const result: CreateJobResponse = await response.json();

      // Add job to polling system
      console.log(`🚀 Adding job to polling system: ${result.job_id} (${jobType})`);
      addJobId(result.job_id, jobType);

      // ✅ FIXED: Update processing state with real job ID immediately
      if (processingUuids.length > 0 && stateRef.current.processing.uuids.size > 0) {
        let operation: 'delete' | null = null;

        if (['delete_contacts', 'remove_from_list'].includes(jobType)) {
          operation = 'delete';
        }

        if (operation) {
          console.log(`🔄 Updating processing state with real job ID: ${result.job_id}`);
          dispatch({
            type: 'SET_PROCESSING',
            payload: {
              uuids: processingUuids,
              operation,
              jobId: result.job_id
            }
          });
        }
      }

      // Clear selection after setting processing state
      dispatch({ type: 'CLEAR_SELECTION' });

      return result.job_id;
    } catch (error) {
      console.error(`❌ ${jobType} job failed:`, error);
      // Always clear processing state on any error
      dispatch({ type: 'CLEAR_PROCESSING' });
      throw error;
    }
  }, [apiKey, addJobId, getVisibleUUIDs, dispatch]);

  // ENHANCED: unlockSelectedContacts with pay-as-you-go support
  const unlockSelectedContacts = useCallback(async (owner_id: string, list_ids: string[], selectAll: boolean) => {
    try {
      // First, check if pay-as-you-go is available
      const payAsYouGoStatus = await fetch(`${API_BASE_URL}/api/pay-as-you-go/status`, {
        headers: { 'Authorization': `Bearer ${owner_id}` }
      }).then(res => res.ok ? res.json() : null).catch(() => null);

      if (payAsYouGoStatus?.success && payAsYouGoStatus.can_use_pay_as_you_go) {
        // Use pay-as-you-go billing
        const selectedCount = selectAll ? state.totalResults : state.selection.selectedIds.size;
        const filters = selectAll ? getCompleteFilters() : undefined;
        const person_uuids = selectAll ? undefined : Array.from(state.selection.selectedIds);

        const requestBody: Record<string, unknown> = { limit: selectedCount };
        if (person_uuids && person_uuids.length > 0) {
          requestBody.person_uuids = person_uuids;
        } else if (filters && Object.keys(filters).length > 0) {
          requestBody.filters = filters;
        } else {
          throw new Error('Must select contacts or have active filters');
        }

        // ✅ FIXED: Set processing state for pay-as-you-go operations
        const currentState = stateRef.current;
        let processingUuids: string[] = [];

        // Determine which UUIDs to track
        if (selectAll) {
          processingUuids = getVisibleUUIDs();
          console.log(`🔄 Using visible UUIDs for "Select All" pay-as-you-go processing: ${processingUuids.length} items`);
        } else {
          processingUuids = Array.from(currentState.selection.selectedIds);
          console.log(`🔄 Using selected UUIDs for pay-as-you-go processing: ${processingUuids.length} items`);
        }

        // Clear any existing processing state first
        if (currentState.processing.uuids.size > 0) {
          console.log(`🧹 Clearing existing processing state (${currentState.processing.uuids.size} UUIDs)`);
          dispatch({ type: 'CLEAR_PROCESSING' });
        }

        // Set processing state for pay-as-you-go operation
        if (processingUuids.length > 0) {
          const tempJobId = `pending_${Date.now()}`;
          console.log(`🔄 Setting pay-as-you-go processing state: ${processingUuids.length} UUIDs, tempJobId: ${tempJobId}`);
          dispatch({
            type: 'SET_PROCESSING',
            payload: {
              uuids: processingUuids,
              operation: null,
              jobId: tempJobId
            }
          });
        }

        // Use pay-as-you-go endpoint
        const response = await fetch(`${API_BASE_URL}/api/pay-as-you-go/unlock-contacts`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${owner_id}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Clear processing state on error
          console.log(`❌ Pay-as-you-go unlock failed, clearing processing state`);
          dispatch({ type: 'CLEAR_PROCESSING' });
          throw new Error(errorData.detail || `Pay-as-you-go unlock failed: ${response.status}`);
        }

        const result = await response.json();
        console.log('🔍 Pay-as-you-go response:', result);
        
        // Pay-as-you-go creates a job, so we should track it
        if (result.job_id) {
          console.log(`🚀 Pay-as-you-go job created: ${result.job_id}, adding to polling system`);
          addJobId(result.job_id, 'contacts_unlock');
          console.log(`✅ Job ${result.job_id} added to polling system`);
          
          // Update processing state with real job ID
          if (processingUuids.length > 0 && stateRef.current.processing.uuids.size > 0) {
            console.log(`🔄 Updating processing state with pay-as-you-go job ID: ${result.job_id}`);
            dispatch({
              type: 'SET_PROCESSING',
              payload: {
                uuids: processingUuids,
                operation: null,
                jobId: result.job_id
              }
            });
          }
          
          // Clear selection after setting processing state
          dispatch({ type: 'CLEAR_SELECTION' });
        } else {
          // Fallback for immediate completion (shouldn't happen with current backend)
          const message = result.debt_created 
            ? `Unlocked ${result.contacts_unlocked} contacts. Debt created: $${result.debt_created}`
            : `Unlocked ${result.contacts_unlocked} contacts. Charged: $${result.total_charged}`;
          
          showToast({ 
            type: 'success', 
            title: 'Contacts Unlocked', 
            message 
          });

          // Clear processing state after successful operation
          console.log(`✅ Pay-as-you-go unlock completed, clearing processing state`);
          dispatch({ type: 'CLEAR_PROCESSING' });
          
          // Clear selection after successful operation
          dispatch({ type: 'CLEAR_SELECTION' });
        }
        
      } else {
        // Fall back to regular job-based billing
        const currentBalance = state.billingBalance;
        if (currentBalance && typeof currentBalance.balance === 'number') {
          const selectedCount = selectAll ? state.totalResults : state.selection.selectedIds.size;
          const estimatedCost = selectedCount * 0.02; // $0.02 per contact

          // Check if balance is sufficient (allowing -$10.00 max debt)
          if (currentBalance.balance - estimatedCost < -10.00) {
            setInsufficientBalanceModal({
              isOpen: true,
              currentBalance: currentBalance.balance,
              requiredAmount: estimatedCost
            });
            return;
          }
        }

        const filters = selectAll ? getCompleteFilters() : undefined;
        const person_uuids = selectAll ? undefined : Array.from(state.selection.selectedIds);

        const requestBody: Record<string, unknown> = { list_ids };
        if (person_uuids && person_uuids.length > 0) {
          requestBody.person_uuids = person_uuids;
        } else if (filters && Object.keys(filters).length > 0) {
          requestBody.filters = filters;
        } else {
          throw new Error('Must select contacts or have active filters');
        }

        await makeJobRequest(`${API_BASE_URL}/api/contacts/unlock-jobs`, requestBody, 'contacts_unlock');
      }

    } catch (error) {
      // Clear processing state on any error
      console.log(`❌ Unlock operation failed, clearing processing state`);
      dispatch({ type: 'CLEAR_PROCESSING' });
      
      showToast({
        type: 'error',
        title: 'Unlock Failed',
        message: error instanceof Error ? error.message : 'Failed to start unlock job'
      });
    }
  }, [state.totalResults, state.selection.selectedIds, state.billingBalance, getCompleteFilters, makeJobRequest, showToast, getVisibleUUIDs, dispatch, addJobId]);

  const addSelectedContactsToList = useCallback(async (_owner_id: string, list_ids: string[]) => {
    try {
      const contact_uuids = Array.from(state.selection.selectedIds);

      if (contact_uuids.length === 0) {
        throw new Error('No contacts selected');
      }

      const requestBody = { contact_uuids, list_ids };

      await makeJobRequest(`${API_BASE_URL}/api/lists/contacts:add`, requestBody, 'add_to_list');

    } catch (error) {
      showToast({
        type: 'error',
        title: 'Add to List Failed',
        message: error instanceof Error ? error.message : 'Failed to start add to list job'
      });
    }
  }, [makeJobRequest, showToast, state.selection.selectedIds]);



  const deleteContactsJobAsync = useCallback(async () => {
    showConfirmationDialog({
      title: 'Delete Contacts',
      message: `Are you sure you want to permanently delete ${state.selection.selectAll ? 'all filtered' : state.selection.selectedIds.size} contact${state.selection.selectAll || state.selection.selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.`,
      confirmText: 'Delete Contacts',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const filters = state.selection.selectAll ? getCompleteFilters() : undefined;
          const contact_uuids = state.selection.selectAll ? undefined : Array.from(state.selection.selectedIds);

          const requestBody: Record<string, unknown> = {};
          if (contact_uuids && contact_uuids.length > 0) {
            requestBody.contact_uuids = contact_uuids;
          } else if (filters !== undefined) {
            requestBody.filters = filters;
            requestBody.select_all = true;
          } else {
            throw new Error('Must select contacts or have active filters');
          }

          await makeJobRequest(`${API_BASE_URL}/api/contacts/delete/job`, requestBody, 'delete_contacts');

        } catch (error) {
          showToast({
            type: 'error',
            title: 'Delete Failed',
            message: error instanceof Error ? error.message : 'Failed to start delete job'
          });
        }
        hideConfirmationDialog();
      },
      onCancel: hideConfirmationDialog
    });
  }, [makeJobRequest, showToast, showConfirmationDialog, hideConfirmationDialog, state.selection, getCompleteFilters]);

  const removeFromListJobAsync = useCallback(async (list_id: string) => {
    try {
      const requestBody: Record<string, unknown> = { list_id };

      if (state.selection.selectAll) {
        const filters = getCompleteFilters();
        requestBody.filters = filters;
        requestBody.select_all = true;
      } else {
        requestBody.contact_uuids = Array.from(state.selection.selectedIds);
      }

      if (!requestBody.contact_uuids && !requestBody.select_all) {
        throw new Error('Must select contacts or have active filters');
      }

      await makeJobRequest(`${API_BASE_URL}/api/lists/remove/job`, requestBody, 'remove_from_list');

    } catch (error) {
      showToast({
        type: 'error',
        title: 'Remove Failed',
        message: error instanceof Error ? error.message : 'Failed to start remove job'
      });
    }
  }, [makeJobRequest, showToast, state.selection, getCompleteFilters]);

  const exportSelectedContacts = useCallback(async (_owner_id: string, format: 'json' | 'csv' = 'csv') => {
    try {
      const filters = state.selection.selectAll ? getCompleteFilters() : undefined;
      const selected_uuids = state.selection.selectAll ? undefined : Array.from(state.selection.selectedIds);

      const requestBody = {
        data_source: state.searchState.dataSource,
        format,
        select_all: state.selection.selectAll,
        ...(filters && Object.keys(filters).length > 0 && { filters }),
        ...(selected_uuids && selected_uuids.length > 0 && { selected_uuids })
      };

      await makeJobRequest(`${API_BASE_URL}/api/export/job`, requestBody, 'export');

    } catch (error) {
      showToast({
        type: 'error',
        title: 'Export Failed',
        message: error instanceof Error ? error.message : 'Failed to start export job'
      });
    }
  }, [makeJobRequest, showToast, state.selection, state.searchState.dataSource, getCompleteFilters]);

  const deleteList = useCallback(async (_owner_id: string, list_id: string, list_name: string, onSuccess?: () => void) => {
    showConfirmationDialog({
      title: 'Delete List',
      message: `Are you sure you want to delete the list "${list_name}"? This action cannot be undone.`,
      confirmText: 'Delete List',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/lists/delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
            body: JSON.stringify({ list_id })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to delete list');
          }

          showToast({
            type: 'success',
            title: 'List Deleted',
            message: `List "${list_name}" has been deleted`
          });

          if (onSuccess) onSuccess();
        } catch (error) {
          showToast({
            type: 'error',
            title: 'Delete Failed',
            message: error instanceof Error ? error.message : 'Failed to delete list'
          });
        }
        hideConfirmationDialog();
      },
      onCancel: hideConfirmationDialog
    });
  }, [apiKey, showToast, showConfirmationDialog, hideConfirmationDialog]);

  // State setters
  const setSearchQuery = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  }, []);

  const setPage = useCallback((page: number) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  }, []);

  const setSorting = useCallback((sorting: SortingState) => {
    dispatch({ type: 'SET_SORTING', payload: Array.isArray(sorting) ? sorting : [] });
  }, []);

  const updateFilter = useCallback((field: string, value: FilterValue) => {
    const currentFilters = { ...stateRef.current.searchState.filters };
    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      delete currentFilters[field];
    } else {
      currentFilters[field] = value;
    }
    dispatch({ type: 'SET_FILTERS', payload: currentFilters });
  }, []);

  const clearAllFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_FILTERS' });
  }, []);

  const clearFiltersByCategory = useCallback((category: string) => {
    dispatch({ type: 'CLEAR_FILTERS_BY_CATEGORY', payload: { category } });
  }, []);

  const setColumnVisibility = useCallback((columns: string[]) => {
    dispatch({ type: 'SET_VISIBLE_COLUMNS', payload: columns });
  }, []);

  const setNavigation = useCallback((navigation: NavigationState) => {
    dispatch({ type: 'SET_NAVIGATION', payload: navigation });
  }, []);

  const setDataSource = useCallback((dataSource: DataSourceType) => {
    if (dataSource === 'people') {
      setNavigation({ view: 'people', listId: null });
    } else {
      setNavigation({ view: 'list', listId: null });
    }
    
    // Update visible columns for the new data source
    dispatch({ type: 'UPDATE_VISIBLE_COLUMNS_FOR_DATA_SOURCE' });
  }, [setNavigation, dispatch]);

  const selectItem = useCallback((id: string) => { dispatch({ type: 'SELECT_ITEM', payload: id }); }, []);
  const deselectItem = useCallback((id: string) => { dispatch({ type: 'DESELECT_ITEM', payload: id }); }, []);
  const selectAll = useCallback((select: boolean) => { dispatch({ type: 'SELECT_ALL', payload: select }); }, []);
  const clearSelection = useCallback(() => { dispatch({ type: 'CLEAR_SELECTION' }); }, []);
  const setView = useCallback((view: 'main' | 'terms' | 'privacy' | 'activity' | 'admin' | 'billing') => { 
    console.log(`🔍 AppProvider setView called with: ${view}`);
    dispatch({ type: 'SET_VIEW', payload: view }); 
  }, []);

  const contextValue: AppContextType = {
    state,
    dispatch,
    showToast,
    showConfirmationDialog,
    hideConfirmationDialog,
    setSearchQuery,
    setPage,
    setSorting,
    updateFilter,
    clearAllFilters,
    clearFiltersByCategory,
    setColumnVisibility,
    setView,
    setDataSource,
    setNavigation,
    selectItem,
    deselectItem,
    selectAll,
    clearSelection,
    saveSelectedContacts: async () => { },
    unlockSelectedContacts,
    addSelectedContactsToList,
    removeFromListJobAsync,
    deleteContactsJobAsync,

    deleteList,
    exportSelectedContacts,
    addJobId,
    cancelJob,
    removeJob,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
      <InsufficientBalanceModal
        isOpen={insufficientBalanceModal.isOpen}
        onClose={() => setInsufficientBalanceModal(prev => ({ ...prev, isOpen: false }))}
        currentBalance={insufficientBalanceModal.currentBalance}
        requiredAmount={insufficientBalanceModal.requiredAmount}
        onAddFunds={() => {
          setInsufficientBalanceModal(prev => ({ ...prev, isOpen: false }));
          dispatch({ type: 'SET_VIEW', payload: 'billing' });
        }}
        onUpdatePaymentMethod={() => {
          setInsufficientBalanceModal(prev => ({ ...prev, isOpen: false }));
          dispatch({ type: 'SET_VIEW', payload: 'billing' });
        }}
        hasPaymentMethod={state.billingBalance?.has_payment_method || false}
      />
    </AppContext.Provider>
  );
}

export default AppProvider;